import { Badge, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMedplum, useResource, useSearchResources } from '@medplum/react-hooks';
import { useEffect, useMemo } from 'react';
import { ORG_TYPE_PAYER } from '@unify/fhir-model/constants';
import { getPayerState } from '@unify/fhir-model/resources/organization';
import {
  getCoverageGroupValue,
  getCoverageMemberId,
  getCoverageRelationship,
} from '@unify/fhir-model/resources/coverage';
import { useEffectivePortalPatientId } from '../../../../hooks/usePortalSubjectPatientId';
import type {
  ClientPortalCoverageSummary,
  ClientPortalInsurancePriority,
} from '../../utils/clientPortalFhirTypes';
import {
  saveClientPortalInsurance,
  type ClientPortalInsuranceUpdateInput,
} from '../../api/clientPortalBillingActions';
import {
  insurancePriorityBadgeColor,
  insurancePriorityLabel,
  patientHomeState,
  payerMatchesClientState,
} from '../../utils/portalBillingModels';

const RELATIONSHIP_OPTIONS = [
  { value: 'Self', label: 'Self' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Child', label: 'Child' },
  { value: 'Other', label: 'Other' },
];

export interface EditInsuranceModalProps {
  opened: boolean;
  coverage: ClientPortalCoverageSummary | null;
  onClose: () => void;
  onError: (message: string) => void;
  onSaved?: () => void;
}

export function EditInsuranceModal({
  opened,
  coverage,
  onClose,
  onError,
  onSaved,
}: EditInsuranceModalProps): JSX.Element {
  const medplum = useMedplum();
  const patientId = useEffectivePortalPatientId();
  const patient = useResource(patientId ? { reference: `Patient/${patientId}` } : undefined);
  const clientState = patientHomeState(patient?.resourceType === 'Patient' ? patient : undefined);
  const [payerOrgs] = useSearchResources('Organization', {
    type: ORG_TYPE_PAYER,
    _count: '200',
    _sort: 'name',
  });

  const form = useForm<ClientPortalInsuranceUpdateInput>({
    initialValues: {
      patientId: patientId ?? '',
      priority: 'primary',
      payerOrgId: '',
      payerName: '',
      memberId: '',
      groupNumber: '',
      planName: '',
      relationship: 'Self',
      subscriberFirstName: '',
      subscriberLastName: '',
      subscriberDateOfBirth: '',
      periodStart: '',
      periodEnd: '',
    },
    validate: {
      payerOrgId: (value) => (value.trim() ? null : 'Insurance company is required'),
      memberId: (value) => (value.trim() ? null : 'Member ID is required'),
      subscriberFirstName: (value, values) =>
        values.relationship !== 'Self' && !value?.trim()
          ? 'Subscriber first name is required'
          : null,
      subscriberLastName: (value, values) =>
        values.relationship !== 'Self' && !value?.trim()
          ? 'Subscriber last name is required'
          : null,
      subscriberDateOfBirth: (value, values) =>
        values.relationship !== 'Self' && !value?.trim()
          ? 'Subscriber date of birth is required'
          : null,
    },
  });

  const payerOptions = useMemo(() => {
    const selectedId = form.values.payerOrgId;
    return (payerOrgs ?? [])
      .filter((o) => o.id && o.name)
      .filter((o) => o.id === selectedId || payerMatchesClientState(getPayerState(o), clientState))
      .map((o) => ({ value: o.id as string, label: o.name as string }));
  }, [payerOrgs, clientState, form.values.payerOrgId]);

  useEffect(() => {
    if (!opened || !coverage) return;
    const payerMatch = payerOptions.find(
      (opt) => opt.label.toLowerCase() === coverage.payerName.toLowerCase(),
    );
    const relationshipLabel =
      coverage.relationship === 'self' || coverage.relationship === 'Self'
        ? 'Self'
        : coverage.relationship === 'spouse' || coverage.relationship === 'Spouse'
          ? 'Spouse'
          : coverage.relationship === 'child' || coverage.relationship === 'Child'
            ? 'Child'
            : coverage.relationship === 'other' || coverage.relationship === 'Other'
              ? 'Other'
              : 'Self';
    form.setValues({
      patientId: patientId ?? '',
      coverageId: coverage.id.startsWith('new-') ? undefined : coverage.id,
      priority: coverage.priority ?? 'primary',
      payerOrgId: payerMatch?.value ?? '',
      payerName: coverage.payerName === '' ? '' : coverage.payerName,
      memberId: '',
      groupNumber: '',
      planName: coverage.planName ?? '',
      relationship: relationshipLabel,
      subscriberFirstName: '',
      subscriberLastName: '',
      subscriberDateOfBirth: '',
      periodStart: coverage.periodStart ?? '',
      periodEnd: coverage.periodEnd ?? '',
    });
    form.clearErrors();

    if (coverage.id.startsWith('new-')) return;
    void (async () => {
      try {
        const cov = await medplum.readResource('Coverage', coverage.id);
        const orgId = cov.payor?.[0]?.reference?.startsWith('Organization/')
          ? cov.payor[0].reference.slice('Organization/'.length)
          : payerMatch?.value ?? '';
        const rel = getCoverageRelationship(cov);
        const relLabel =
          rel === 'spouse' ? 'Spouse' : rel === 'child' ? 'Child' : rel === 'other' ? 'Other' : 'Self';
        let subscriberFirstName = '';
        let subscriberLastName = '';
        let subscriberDateOfBirth = '';
        const subRef = cov.subscriber?.reference;
        if (subRef?.startsWith('RelatedPerson/')) {
          const rp = await medplum.readResource('RelatedPerson', subRef.slice('RelatedPerson/'.length));
          subscriberFirstName = rp.name?.[0]?.given?.[0] ?? '';
          subscriberLastName = rp.name?.[0]?.family ?? '';
          subscriberDateOfBirth = rp.birthDate ?? '';
        }
        form.setFieldValue('payerOrgId', orgId);
        form.setFieldValue('memberId', getCoverageMemberId(cov) ?? '');
        form.setFieldValue('groupNumber', getCoverageGroupValue(cov) ?? '');
        form.setFieldValue('relationship', relLabel);
        form.setFieldValue('subscriberFirstName', subscriberFirstName);
        form.setFieldValue('subscriberLastName', subscriberLastName);
        form.setFieldValue('subscriberDateOfBirth', subscriberDateOfBirth);
        if (cov.period?.start) form.setFieldValue('periodStart', cov.period.start);
        if (cov.period?.end) form.setFieldValue('periodEnd', cov.period.end);
      } catch {
        // Keep the summary-hydrated draft if the Coverage read fails.
      }
    })();
  }, [opened, coverage, patientId, payerOptions]);

  useEffect(() => {
    const selected = payerOptions.find((opt) => opt.value === form.values.payerOrgId);
    if (selected && form.values.payerName !== selected.label) {
      form.setFieldValue('payerName', selected.label);
    }
  }, [form.values.payerOrgId, payerOptions]);

  const handleSubmit = form.onSubmit(async (values) => {
    if (!patientId) {
      onError('Could not determine which patient profile to update.');
      return;
    }
    try {
      await saveClientPortalInsurance(medplum, { ...values, patientId });
      onSaved?.();
      onClose();
    } catch {
      onError('Could not update insurance. Please contact your care team for help.');
    }
  });

  const priority = (form.values.priority ?? coverage?.priority ?? 'primary') as ClientPortalInsurancePriority;
  const isNewPlan = coverage?.id.startsWith('new-') ?? false;
  const showSubscriberFields = form.values.relationship !== 'Self';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm" wrap="nowrap">
          {isNewPlan ? 'Add insurance plan' : 'Update insurance'}
          <Badge size="lg" variant="light" color={insurancePriorityBadgeColor(priority)}>
            {insurancePriorityLabel(priority)}
          </Badge>
        </Group>
      }
      centered
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Select
            label="Insurance company"
            placeholder="Select your insurance company"
            description={
              clientState ? `Showing insurers for ${clientState}` : undefined
            }
            searchable
            nothingFoundMessage={
              clientState ? `No insurers found for ${clientState}` : 'No payers found'
            }
            data={payerOptions}
            required
            {...form.getInputProps('payerOrgId')}
          />
          <TextInput
            label="Member ID"
            placeholder="Enter member ID"
            description={
              coverage?.memberIdMasked
                ? `Current ID on file: ${coverage.memberIdMasked}`
                : undefined
            }
            required
            {...form.getInputProps('memberId')}
          />
          <TextInput
            label="Group number"
            placeholder="Optional"
            {...form.getInputProps('groupNumber')}
          />
          <TextInput
            label="Plan name"
            placeholder="Optional"
            {...form.getInputProps('planName')}
          />
          <Select
            label="Relationship to subscriber"
            data={RELATIONSHIP_OPTIONS}
            {...form.getInputProps('relationship')}
          />
          {showSubscriberFields && (
            <>
              <Text size="sm" c="dimmed">
                Tell us about the primary policyholder so we can verify your coverage.
              </Text>
              <TextInput
                label="Subscriber first name"
                required
                {...form.getInputProps('subscriberFirstName')}
              />
              <TextInput
                label="Subscriber last name"
                required
                {...form.getInputProps('subscriberLastName')}
              />
              <TextInput
                label="Subscriber date of birth"
                type="date"
                required
                {...form.getInputProps('subscriberDateOfBirth')}
              />
            </>
          )}
          <TextInput
            label="Coverage start date"
            type="date"
            {...form.getInputProps('periodStart')}
          />
          <TextInput
            label="Coverage end date"
            type="date"
            description="Optional — leave blank if your plan has no end date."
            {...form.getInputProps('periodEnd')}
          />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" color="gold">
              {isNewPlan ? 'Add insurance' : 'Save insurance'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
